import { useRef, useCallback, useState } from "react";
import { supabase } from "@/lib/supabase";

export function useCallRecorder() {
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const [isRecording, setIsRecording] = useState(false);

  const startRecording = useCallback(
    (localStream: MediaStream, remoteStream?: MediaStream) => {
      try {
        const ctx = new AudioContext();
        const dest = ctx.createMediaStreamDestination();

        // Mix local mic
        const localSource = ctx.createMediaStreamSource(localStream);
        localSource.connect(dest);

        // Mix remote audio if available
        if (remoteStream) {
          const remoteSource = ctx.createMediaStreamSource(remoteStream);
          remoteSource.connect(dest);
        }

        const recorder = new MediaRecorder(dest.stream, {
          mimeType: MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
            ? "audio/webm;codecs=opus"
            : "audio/webm",
        });

        chunksRef.current = [];
        recorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunksRef.current.push(e.data);
        };

        recorder.start(1000); // chunk every second
        recorderRef.current = recorder;
        setIsRecording(true);
        console.log("🎙️ Call recording started");
      } catch (err) {
        console.error("Failed to start recording:", err);
      }
    },
    []
  );

  const stopAndUpload = useCallback(
    async (callDbId: string): Promise<string | null> => {
      return new Promise((resolve) => {
        const recorder = recorderRef.current;
        if (!recorder || recorder.state === "inactive") {
          setIsRecording(false);
          resolve(null);
          return;
        }

        recorder.onstop = async () => {
          setIsRecording(false);
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          chunksRef.current = [];

          if (blob.size < 1000) {
            console.log("Recording too small, skipping upload");
            resolve(null);
            return;
          }

          const filePath = `${callDbId}.webm`;
          const { error: uploadErr } = await supabase.storage
            .from("call-recordings")
            .upload(filePath, blob, { contentType: "audio/webm", upsert: true });

          if (uploadErr) {
            console.error("Upload recording error:", uploadErr);
            resolve(null);
            return;
          }

          // Get signed URL (valid 1 year)
          const { data: urlData } = await supabase.storage
            .from("call-recordings")
            .createSignedUrl(filePath, 365 * 24 * 60 * 60);

          const url = urlData?.signedUrl || null;

          // Save to calls table
          if (url) {
            await supabase
              .from("calls")
              .update({ recording_url: url } as any)
              .eq("id", callDbId);
          }

          console.log("✅ Recording uploaded:", filePath);
          resolve(url);
        };

        recorder.stop();
      });
    },
    []
  );

  const cancel = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    chunksRef.current = [];
    setIsRecording(false);
  }, []);

  return { isRecording, startRecording, stopAndUpload, cancel };
}
